import React, { useCallback, useRef } from "react";

/**
 * Lightweight 3D tilt-on-hover wrapper. Updates CSS variables on mousemove
 * and resets on leave. Pair with `.tilt-card` styles in App.css.
 */
const TiltCard = ({
  className = "",
  intensity = 7,
  scale = 1.015,
  children,
  ...rest
}) => {
  const ref = useRef(null);
  const rafRef = useRef(0);

  const setVars = useCallback((rx, ry, sc, glowX, glowY) => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--tilt-rx", `${rx}deg`);
    el.style.setProperty("--tilt-ry", `${ry}deg`);
    el.style.setProperty("--tilt-scale", `${sc}`);
    el.style.setProperty("--tilt-glow-x", `${glowX}%`);
    el.style.setProperty("--tilt-glow-y", `${glowY}%`);
  }, []);

  const onMove = useCallback(
    (e) => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const px = (e.clientX - rect.left) / rect.width;
      const py = (e.clientY - rect.top) / rect.height;
      const rx = (0.5 - py) * intensity;
      const ry = (px - 0.5) * intensity;
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() =>
        setVars(rx, ry, scale, px * 100, py * 100),
      );
    },
    [intensity, scale, setVars],
  );

  const onLeave = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    setVars(0, 0, 1, 50, 50);
  }, [setVars]);

  return (
    <div
      ref={ref}
      className={`tilt-card ${className}`}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      {...rest}
    >
      <div className="tilt-card-inner">{children}</div>
      <div className="tilt-card-glow" aria-hidden />
    </div>
  );
};

export default TiltCard;
